// =============================================================================
// ResourcePagesAdmin.jsx — Manage → Resource Pages (Supervisors + SuperUsers)
// =============================================================================
// Authors the Resources content: a nestable tree of pages per section (Invoicing
// / Responders), edited with the Quill WYSIWYG (ResourceEditor). Viewing is
// read-only under Resources → Invoicing / Responders (ResourceSection). RLS
// restricts writes to admins; this screen is admin-gated by the route. (db-109)
// =============================================================================

import React from "react";
import {
  createResourcePage,
  deactivateResourcePages,
  listResourcePages,
  updateResourcePage,
} from "../../data/resourcePages";
import { confirmDialog } from "../../services/dialogService";
import { sanitizeResourceHtml } from "../../services/resourceHtml";
import { buildResourceTree, descendantIds, flattenResourceTree } from "../../services/resourceTree";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";
import { ResourceEditor } from "../resources/ResourceEditor.jsx";
import { ResourceDocumentsPanel } from "./ResourceDocumentsPanel.jsx";

const SECTIONS = [
  { key: "general", label: "General" },
  { key: "invoicing", label: "Invoicing" },
  { key: "responders", label: "Responders" },
];

function emptyForm() {
  return { title: "", parent_id: "", body: "" };
}

export function ResourcePagesAdmin({ user }) {
  const [section, setSection] = React.useState("general");
  // The Documents tab (db-138) is a sibling of the three SECTION tabs but isn't a
  // section — documents are one shared library, linkable from any section. Kept as
  // its own flag rather than a fourth `section` value so the page-loading effect
  // and the tree state below are untouched (and survive a trip to Documents).
  const [showDocs, setShowDocs] = React.useState(false);
  const [rows, setRows] = React.useState(null);
  const [selId, setSelId] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm());
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState("");

  const load = React.useCallback(
    function () {
      return listResourcePages(section).then(function (r) {
        const list = r || [];
        setRows(list);
        return list;
      });
    },
    [section]
  );

  // Reload whenever the section changes; clear selection.
  React.useEffect(
    function () {
      setSelId(null);
      setForm(emptyForm());
      setDirty(false);
      load();
    },
    [load]
  );

  const flat = React.useMemo(
    function () {
      return rows ? flattenResourceTree(buildResourceTree(rows)) : [];
    },
    [rows]
  );

  function loadIntoForm(row) {
    setForm({ title: row.title, parent_id: row.parent_id || "", body: row.body || "" });
    setDirty(false);
  }

  async function selectPage(id) {
    if (dirty && !(await confirmDialog({ message: "Discard unsaved changes to this page?" })))
      return;
    const row = (rows || []).find((r) => r.id === id);
    setSelId(id);
    if (row) loadIntoForm(row);
  }

  function setField(k, v) {
    setForm(function (f) {
      return { ...f, [k]: v };
    });
    setDirty(true);
  }

  async function addPage(parentId) {
    if (dirty && !(await confirmDialog({ message: "Discard unsaved changes to this page?" })))
      return;
    const siblings = (rows || []).filter((r) => (r.parent_id || null) === (parentId || null));
    const nextOrder = siblings.reduce((m, r) => Math.max(m, r.sort_order), -1) + 1;
    const res = await createResourcePage({
      section,
      parent_id: parentId || null,
      title: "New page",
      body: "",
      sort_order: nextOrder,
      created_by: user?.id || null,
    });
    if (res.error || !res.data) {
      setMsg(`Add failed: ${res.error?.message || "unknown"}`);
      return;
    }
    logAudit("resource_page_created", { id: res.data.id, section, by: user?.shelterName });
    await load();
    setSelId(res.data.id);
    loadIntoForm(res.data);
  }

  async function save() {
    if (!selId) return;
    if (!form.title.trim()) {
      setMsg("Please enter a title.");
      return;
    }
    setSaving(true);
    setMsg("");
    const res = await updateResourcePage(selId, {
      title: form.title.trim(),
      parent_id: form.parent_id || null,
      body: sanitizeResourceHtml(form.body || ""),
    });
    setSaving(false);
    if (res.error) {
      setMsg(`Save failed: ${res.error.message}`);
      return;
    }
    logAudit("resource_page_updated", { id: selId, section, by: user?.shelterName });
    setDirty(false);
    setMsg("Saved ✓");
    setTimeout(() => setMsg(""), 3000);
    load();
  }

  async function del() {
    if (!selId) return;
    const kids = descendantIds(rows || [], selId);
    const ok = await confirmDialog({
      title: "Delete page",
      message:
        kids.length > 0
          ? `Delete this page and its ${kids.length} sub-page(s)? This can't be undone from here.`
          : "Delete this page? This can't be undone from here.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const res = await deactivateResourcePages([selId, ...kids]);
    if (res.error) {
      setMsg(`Delete failed: ${res.error.message}`);
      return;
    }
    logAudit("resource_page_deleted", {
      id: selId,
      section,
      count: kids.length + 1,
      by: user?.shelterName,
    });
    setSelId(null);
    setForm(emptyForm());
    setDirty(false);
    load();
  }

  // Reorder a page among its siblings by rewriting the sibling sort_orders.
  async function move(id, dir) {
    const row = (rows || []).find((r) => r.id === id);
    if (!row) return;
    const siblings = (rows || [])
      .filter((r) => (r.parent_id || null) === (row.parent_id || null))
      .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title));
    const i = siblings.findIndex((r) => r.id === id);
    const j = i + dir;
    if (j < 0 || j >= siblings.length) return;
    const reordered = siblings.slice();
    const tmp = reordered[i];
    reordered[i] = reordered[j];
    reordered[j] = tmp;
    // Persist sequential sort_orders for the affected siblings.
    for (let k = 0; k < reordered.length; k++) {
      if (reordered[k].sort_order !== k) {
        await updateResourcePage(reordered[k].id, { sort_order: k });
      }
    }
    load();
  }

  // Parent options for the current page: everything in the section EXCEPT the
  // page itself and its descendants (no cycles).
  const parentOptions = React.useMemo(
    function () {
      if (!rows) return [];
      const banned = new Set(selId ? [selId, ...descendantIds(rows, selId)] : []);
      return flattenResourceTree(buildResourceTree(rows)).filter((f) => !banned.has(f.node.id));
    },
    [rows, selId]
  );

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <h1 className="m-0 mb-4 text-[22px] text-zephyr-text">Resource Pages</h1>

      {/* Section tabs */}
      <div className="flex gap-0 mb-4 border-b-2 border-zephyr-border">
        {SECTIONS.map(function (s) {
          const active = !showDocs && section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={function () {
                setShowDocs(false);
                setSection(s.key);
              }}
              className={[
                "bg-transparent border-0 py-[10px] px-5 text-[14px] cursor-pointer -mb-[2px]",
                active
                  ? "border-b-[3px] border-b-zephyr-teal font-bold text-zephyr-teal"
                  : "border-b-[3px] border-b-transparent font-normal text-zephyr-muted",
              ].join(" ")}
              style={{ fontFamily: "inherit" }}
            >
              {s.label}
            </button>
          );
        })}
        {/* Spacer, so Documents reads as a different KIND of tab (a shared library)
            rather than a fourth content section. */}
        <div className="flex-1" />
        <button
          type="button"
          onClick={function () {
            setShowDocs(true);
          }}
          className={[
            "bg-transparent border-0 py-[10px] px-5 text-[14px] cursor-pointer -mb-[2px]",
            showDocs
              ? "border-b-[3px] border-b-zephyr-teal font-bold text-zephyr-teal"
              : "border-b-[3px] border-b-transparent font-normal text-zephyr-muted",
          ].join(" ")}
          style={{ fontFamily: "inherit" }}
        >
          📄 Documents
        </button>
      </div>

      {!showDocs && msg && (
        <div
          className={`mb-3 text-[13px] font-semibold ${
            msg.startsWith("Save failed") ||
            msg.startsWith("Add failed") ||
            msg.startsWith("Delete failed") ||
            msg.startsWith("Please")
              ? "text-[#C62828]"
              : "text-[#2E7D32]"
          }`}
        >
          {msg}
        </div>
      )}

      {showDocs ? (
        <ResourceDocumentsPanel user={user} />
      ) : rows === null ? (
        <div className="text-zephyr-muted p-5">Loading…</div>
      ) : (
        <div className="grid gap-6" style={{ gridTemplateColumns: "minmax(220px, 300px) 1fr" }}>
          {/* Tree + controls */}
          <div className="border border-zephyr-border rounded-[10px] p-2 self-start bg-white">
            <button
              type="button"
              onClick={function () {
                addPage(null);
              }}
              className="w-full text-left text-[13px] font-semibold text-zephyr-teal py-[6px] px-2 cursor-pointer bg-transparent border-none"
            >
              + New top-level page
            </button>
            {flat.length === 0 && (
              <div className="text-[12px] text-zephyr-muted p-2">No pages yet.</div>
            )}
            {flat.map(function (f) {
              const active = f.node.id === selId;
              return (
                <div
                  key={f.node.id}
                  className="flex items-center gap-1"
                  style={{ paddingLeft: f.depth * 14 }}
                >
                  <button
                    type="button"
                    onClick={function () {
                      selectPage(f.node.id);
                    }}
                    className={`flex-1 text-left text-[13px] rounded-[6px] py-[5px] px-2 cursor-pointer border-none ${
                      active
                        ? "bg-zephyr-teal text-white font-semibold"
                        : "text-zephyr-text bg-transparent"
                    }`}
                  >
                    {f.node.title}
                  </button>
                  <button
                    type="button"
                    title="Move up"
                    onClick={function () {
                      move(f.node.id, -1);
                    }}
                    className="text-[11px] text-zephyr-muted bg-transparent border-none cursor-pointer px-1"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    title="Move down"
                    onClick={function () {
                      move(f.node.id, 1);
                    }}
                    className="text-[11px] text-zephyr-muted bg-transparent border-none cursor-pointer px-1"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    title="Add sub-page"
                    onClick={function () {
                      addPage(f.node.id);
                    }}
                    className="text-[13px] text-zephyr-teal bg-transparent border-none cursor-pointer px-1"
                  >
                    +
                  </button>
                </div>
              );
            })}
          </div>

          {/* Editor */}
          <div className="border border-zephyr-border rounded-[10px] p-5 bg-white min-w-0">
            {!selId ? (
              <div className="text-zephyr-muted text-[14px]">
                Select a page to edit, or add a new one.
              </div>
            ) : (
              <>
                <label
                  htmlFor="rp-title"
                  className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
                >
                  Title
                </label>
                <input
                  id="rp-title"
                  type="text"
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  className="w-full max-w-[520px] py-[8px] px-[10px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white mb-3"
                />

                <label
                  htmlFor="rp-parent"
                  className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
                >
                  Parent page (for nesting)
                </label>
                <select
                  id="rp-parent"
                  value={form.parent_id}
                  onChange={(e) => setField("parent_id", e.target.value)}
                  className="w-full max-w-[520px] py-[8px] px-[10px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white mb-4"
                >
                  <option value="">— Top level —</option>
                  {parentOptions.map((f) => (
                    <option key={f.node.id} value={f.node.id}>
                      {`${"— ".repeat(f.depth)}${f.node.title}`}
                    </option>
                  ))}
                </select>

                <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] mb-1">
                  Content
                </div>
                <ResourceEditor value={form.body} onChange={(html) => setField("body", html)} />

                <div className="flex gap-2 items-center mt-4">
                  <Btn onClick={save} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </Btn>
                  <button
                    type="button"
                    onClick={del}
                    className="bg-white text-[#C62828] rounded-[8px] px-4 py-[7px] text-[13px] font-bold cursor-pointer"
                    style={{ border: "1px solid #E0A0A0" }}
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
